import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { sendPageView } from '../lib/analytics'
import { pdfToolFaqItems } from './PdfToolFaq'
import { tools } from '../data/tools'
import { buildCanonicalUrl, buildFaqStructuredData, buildHomeStructuredData, buildToolStructuredData } from './seoUtils'
import { staticPageMeta } from '../pages/staticPageMeta'

const siteName = 'Parentool 工具站'
const defaultTitle = 'Parentool - 亲子资料与实用工具'
const defaultDescription = 'Parentool 提供亲子资料、家庭实用指南、在线工具、纳斯达克看板和亲子资源库。'

const routeMeta: Record<string, { title: string; description: string }> = {
  '/': {
    title: defaultTitle,
    description: defaultDescription
  },
  '/nsdq': {
    title: '纳斯达克定投看板 - Parentool 工具站',
    description: '跟踪纳指估值、趋势、回撤和 ETF 溢价，用评分体系辅助定投节奏判断。'
  },
  '/guides': {
    title: '资料库 - Parentool',
    description: 'Parentool 的亲子手册、出行指南、清单和实用资料。'
  },
  '/guides/': {
    title: '资料库 - Parentool',
    description: 'Parentool 的亲子手册、出行指南、清单和实用资料。'
  }
}

const upsertMeta = (selector: string, create: () => HTMLMetaElement, content: string) => {
  let tag = document.querySelector<HTMLMetaElement>(selector)
  if (!tag) {
    tag = create()
    document.head.appendChild(tag)
  }
  tag.content = content
}

const upsertLink = (selector: string, create: () => HTMLLinkElement, href: string) => {
  let tag = document.querySelector<HTMLLinkElement>(selector)
  if (!tag) {
    tag = create()
    document.head.appendChild(tag)
  }
  tag.href = href
}

const upsertJsonLd = (id: string, payload: object | null) => {
  let script = document.querySelector<HTMLScriptElement>(`script[data-seo-id="${id}"]`)

  if (!payload) {
    script?.remove()
    return
  }

  if (!script) {
    script = document.createElement('script')
    script.type = 'application/ld+json'
    script.dataset.seoId = id
    document.head.appendChild(script)
  }

  script.textContent = JSON.stringify(payload)
}

const Seo = () => {
  const { pathname } = useLocation()

  useEffect(() => {
    const tool = tools.find(item => item.path === pathname)
    const meta = routeMeta[pathname] || staticPageMeta[pathname as keyof typeof staticPageMeta] || (tool ? {
      title: tool.seoTitle || `${tool.name} - ${siteName}`,
      description: tool.seoDescription || tool.description
    } : {
      title: defaultTitle,
      description: defaultDescription
    })

    document.title = meta.title
    upsertMeta('meta[name="description"]', () => {
      const tag = document.createElement('meta')
      tag.name = 'description'
      return tag
    }, meta.description)

    upsertLink('link[rel="canonical"]', () => {
      const tag = document.createElement('link')
      tag.rel = 'canonical'
      return tag
    }, buildCanonicalUrl(pathname))

    upsertMeta('meta[property="og:title"]', () => {
      const tag = document.createElement('meta')
      tag.setAttribute('property', 'og:title')
      return tag
    }, meta.title)

    upsertMeta('meta[property="og:description"]', () => {
      const tag = document.createElement('meta')
      tag.setAttribute('property', 'og:description')
      return tag
    }, meta.description)

    upsertMeta('meta[property="og:url"]', () => {
      const tag = document.createElement('meta')
      tag.setAttribute('property', 'og:url')
      return tag
    }, buildCanonicalUrl(pathname))

    upsertMeta('meta[property="og:type"]', () => {
      const tag = document.createElement('meta')
      tag.setAttribute('property', 'og:type')
      return tag
    }, 'website')

    const jsonLd = pathname === '/'
      ? buildHomeStructuredData()
      : tool
        ? buildToolStructuredData(tool)
        : null

    const faqJsonLd = tool?.categorySlug === 'pdf'
      ? buildFaqStructuredData(pdfToolFaqItems)
      : null

    upsertJsonLd('primary-schema', jsonLd)
    upsertJsonLd('faq-schema', faqJsonLd)
    sendPageView(pathname)
  }, [pathname])

  return null
}

export default Seo
